import { useState, useEffect, useCallback } from "react";
import { CustomField } from "types/CustomField";
import { TemplateEntity } from "types/Template";
import { fetchFileTemplateById } from "containers/files-template-form/files-template-form.service";
import { fetchById } from "services/upload-file.service";
import { transformCustomFieldsSync } from "../build-pdf-utils";

interface UseCustomFieldsParams {
  companyId: string | undefined;
  templateId: string;
  templateIdByFetch: string;
  isEditMode: boolean;
  onSignTypeDetected?: (signType: string) => void;
}

export function useCustomFields({
  companyId,
  templateId,
  templateIdByFetch,
  isEditMode,
  onSignTypeDetected,
}: UseCustomFieldsParams) {
  const [customFields, setCustomFields] = useState<CustomField[]>([]);
  const [templateEntity, setTemplateEntity] = useState<TemplateEntity>();
  const [fileEntitiesMap, setFileEntitiesMap] = useState<{ [key: string]: any[] }>({});
  const [customFieldValuesByTemplate, setCustomFieldValuesByTemplate] = useState<
    Record<string, Record<string, string | string[]>>
  >({});
  const [initialCustomFieldsWhenEditLoaded, setInitialCustomFieldsWhenEditLoaded] =
    useState<CustomField[] | null>(null);
  const [initialTemplateEntityWhenEditLoaded, setInitialTemplateEntityWhenEditLoaded] =
    useState<TemplateEntity | null>(null);

  const handleCustomFieldChange = (index: number, value: string | string[]) => {
    const field = customFields[index];
    if (field && templateId) {
      setCustomFieldValuesByTemplate((prev) => ({
        ...prev,
        [templateId]: { ...(prev[templateId] ?? {}), [field.label]: value },
      }));
    }
    setCustomFields((prev) =>
      prev.map((f, i) => (i === index ? { ...f, value } : f))
    );
  };

  const transformCustomFields = useCallback(
    async (
      apiCustomFields?: Record<string, any>,
      templateFields?: CustomField[]
    ) => {
      const { transformedFields, fileFieldKeys, fileEntities } =
        transformCustomFieldsSync(apiCustomFields, templateFields);

      if (!apiCustomFields) {
        setFileEntitiesMap({});
        return {};
      }

      for (const { key, ids } of fileFieldKeys) {
        if (ids.length > 0 && companyId) {
          try {
            const fileEntitiesPromises = ids.map((fileId: string) =>
              fetchById(fileId, companyId).catch(() => null)
            );
            const fetchedFiles = await Promise.all(fileEntitiesPromises);
            fileEntities[key] = fetchedFiles.filter((file) => file !== null);
          } catch (error) {
            console.error("Error fetching file entities:", error);
            fileEntities[key] = [];
          }
        } else {
          fileEntities[key] = [];
        }
      }

      setFileEntitiesMap(fileEntities);
      return transformedFields;
    },
    [companyId]
  );

  const fetchCustomFieldsForTemplate = useCallback(
    async (
      tid: string,
      savedByTemplate?: Record<string, Record<string, string | string[]>>
    ) => {
      try {
        const fileTemplateEntity = await fetchFileTemplateById(companyId, tid);
        if (fileTemplateEntity?.signType)
          onSignTypeDetected?.(fileTemplateEntity.signType);
        setTemplateEntity(fileTemplateEntity);
        const savedForTemplate = savedByTemplate?.[tid] ?? {};
        setCustomFields(
          fileTemplateEntity.customFields.map((field: CustomField) => {
            const defaultValue = field.type === "file" ? [] : "";
            const preserved = savedForTemplate[field.label];
            return {
              ...field,
              value: preserved !== undefined ? preserved : defaultValue,
            };
          })
        );
      } catch (e) {
        console.error(e);
      }
    },
    [companyId, onSignTypeDetected]
  );

  useEffect(() => {
    if (!templateId) return;
    if (isEditMode && templateId === templateIdByFetch) {
      if (initialCustomFieldsWhenEditLoaded && initialTemplateEntityWhenEditLoaded) {
        setCustomFields(initialCustomFieldsWhenEditLoaded);
        setTemplateEntity(initialTemplateEntityWhenEditLoaded);
      }
      return;
    }
    fetchCustomFieldsForTemplate(templateId, customFieldValuesByTemplate);
  }, [
    templateId,
    templateIdByFetch,
    isEditMode,
    fetchCustomFieldsForTemplate,
    initialCustomFieldsWhenEditLoaded,
    initialTemplateEntityWhenEditLoaded,
  ]);

  return {
    customFields,
    setCustomFields,
    templateEntity,
    setTemplateEntity,
    fileEntitiesMap,
    handleCustomFieldChange,
    transformCustomFields,
    fetchCustomFieldsForTemplate,
    customFieldValuesByTemplate,
    initialCustomFieldsWhenEditLoaded,
    setInitialCustomFieldsWhenEditLoaded,
    initialTemplateEntityWhenEditLoaded,
    setInitialTemplateEntityWhenEditLoaded,
  };
}
